import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  and,
  collection,
  collectionData,
  deleteDoc,
  doc,
  docData,
  Firestore,
  limit as limitFn,
  orderBy,
  query,
  QueryNonFilterConstraint,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import axios from 'axios';
import { NGXLogger } from 'ngx-logger';
import { map, Observable, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppException } from '../models/app-exception.model';
import { GradeLesson } from '../models/grade-lesson.school.model';
import { Lesson } from '../models/lesson.school.model';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SchoolGradeLessonService {
  http = inject(HttpClient);
  logger: NGXLogger = inject(NGXLogger);
  afs: Firestore = inject(Firestore);
  utilsService = inject(UtilsService);

  constructor() {}

  getGradeLessonsByGroup(
    groupId: string,
    gradeId: string,
    academicYear: number,
    lessonStatus?: string[],
    publishBy?: boolean,
    limit?: number,
    lastRecord?: Timestamp,
  ): Observable<GradeLesson[]> {
    this.logger.debug(
      `GradeLessonService :: getGradeLessonsByGroup: groupId: ${groupId}, gradeId: ${gradeId}, academicYear: ${academicYear} (${typeof academicYear}), status: ${lessonStatus}, publishBy: ${publishBy}, limit: ${limit}, lastRecord: ${lastRecord}`,
    );

    try {
      const lessonsRef = collection(
        this.afs,
        `groups/${groupId}/grade_lessons`,
      );

      // base conditions
      const baseConditions: any[] = [where('academicYear', '==', academicYear)];

      if (gradeId) {
        baseConditions.push(where('gradeId', '==', gradeId));
      }

      if (lessonStatus && lessonStatus.length > 0) {
        baseConditions.push(where('lessonStatus', 'in', lessonStatus));
      }

      if (publishBy) {
        baseConditions.push(where('publishBy', '<=', Timestamp.now()));
      }

      // combine all filters under an AND
      const allFilters = and(...baseConditions);

      const orders: QueryNonFilterConstraint[] = [];

      if (publishBy) orders.push(orderBy('publishBy', 'desc'));
      orders.push(orderBy('modified', 'desc'));

      // add ordering, limit, pagination
      const q = query(
        lessonsRef,
        allFilters,
        ...orders,
        ...(limit ? [limitFn(limit)] : []),
        ...(lastRecord ? [startAfter(lastRecord)] : []),
      );

      return collectionData(q, { idField: 'id' }).pipe(take(1));
    } catch (error: any) {
      this.logger.error(
        `GradeLessonService :: getGradeLessonsByGroup: Error: ${error?.toString()}`,
      );
      throw new AppException({
        code: 'ERR_GET_GRADE_LESSONS_BY_GROUP',
        message:
          'Unable to fetch Grade Lessons at this time. Please try again later',
      });
    }
  }

  getGradeLessonById(groupId: string, id: string): Observable<Lesson> {
    const q = doc(this.afs, `groups/${groupId}/grade_lessons/${id}`);
    return docData(q, { idField: 'id' }).pipe(
      take(1),
      map((data) => {
        return data as Lesson;
      }),
    );
  }

  // async createGradeLesson(
  //   groupId: string,
  //   lesson: GradeLesson
  // ): Promise<string> {
  // console.log(
  //   'GradeLessonService :: createGradeLesson:',
  //   JSON.stringify(lesson, null, 2)
  // );
  //   const lessonsRef = collection(this.afs, `groups/${groupId}/grade_lessons`);
  //   return addDoc(lessonsRef, lesson).then((docRef) => docRef.id);
  // }

  async create(
    groupId: string,
    id: string,
    lesson: GradeLesson,
  ): Promise<string> {
    const lessonRef = doc(this.afs, `groups/${groupId}/grade_lessons/${id}`);
    await setDoc(lessonRef, lesson, { merge: true });
    return id;
  }

  async update(
    groupId: string,
    id: string,
    lesson: GradeLesson,
  ): Promise<void> {
    this.logger.debug(
      `GradeLessonService:: update: ${id}: `,
      JSON.stringify(lesson, null, 2),
    );
    const lessonRef = doc(
      this.afs,
      `groups/${groupId}/grade_lessons/${id}`,
    ) as any;
    return updateDoc(lessonRef, { ...lesson });
  }

  async getNotes(url: string) {
    // console.log('GradeLessonService:: getNotes: ', url);
    // const config: AxiosRequestConfig = {
    //   method: 'GET',
    //   headers: {
    //     'Content-Type': 'application/json',
    //   },
    // };
    return axios(url).then((response) => response.data);
  }

  async getNotesUploadUrl(params: any): Promise<any> {
    return this.utilsService
      .setAxiosHeaders(true)
      .then((headers) => {
        return axios.get<any>(
          environment['AWS_SCHOOL_GRADELESSONNOTES_SIGNEDURL']!,
          {
            params: params,
            headers: headers,
          },
        );
      })
      .then((response) => {
        console.log('GradeLessonService:: getNotesUploadUrl:', response.data);
        return {
          method: 'PUT',
          url: response.data.url,
          path: response.data.path,
          headers: {
            'Content-Type': `application/json`,
          },
        };
      });
  }

  async uploadNotes(url: string, data: any): Promise<any> {
    try {
      return axios
        .put(url, data, {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
          },
        })
        .then((response) => {
          this.logger.debug('LessonService:: uploadLesson:', response);
          return response;
        });
    } catch (error) {
      this.logger.error('LessonService:: uploadLesson:', JSON.stringify(error));
    }
  }

  async delete(groupId: string, id: string): Promise<void> {
    const lessonRef = doc(this.afs, `groups/${groupId}/grade_lessons/${id}`);
    return deleteDoc(lessonRef);
  }
}
